import { useMemo, useState, type MouseEvent } from 'react';
import { Box, Button, ListSubheader, Menu, MenuItem, Radio } from '@mui/material';
import { ExpandDropdownIcon } from '../icons/ExpandArrowIcon';

export type TimeFormat = '12h' | '24h';
export type TimeZone = 'utc' | 'local';

type TimeSettingsDropDownProps = {
  onValueChangeCallback: (timeZone: TimeZone, timeFormat: TimeFormat) => void;
  timeZone: TimeZone;
  timeFormat: TimeFormat;
};

const options = [
  {
    group: 'Time Zone',
    values: [
      { label: 'UTC', value: 'utc' },
      { label: 'My Time Zone', value: 'local' },
    ],
  },
  {
    group: 'Time Format',
    values: [
      { label: '12 hour', value: '12h' },
      { label: '24 hour', value: '24h' },
    ],
  },
] as const;

const TimeSettingsDropDown = ({
  onValueChangeCallback,
  timeZone,
  timeFormat,
}: TimeSettingsDropDownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const displayLabel = useMemo(() => {
    const timeZoneLabel =
      options[0].values.find((value) => value.value === timeZone)?.label || 'Time Zone';
    const formatLabel =
      options[1].values.find((value) => value.value === timeFormat)?.label || 'Time Format';
    return `${timeZoneLabel} / ${formatLabel}`;
  }, [timeFormat, timeZone]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const selectTimeZone = (value: TimeZone) => {
    onValueChangeCallback(value, timeFormat);
    handleClose();
  };
  const selectTimeFormat = (value: TimeFormat) => {
    onValueChangeCallback(timeZone, value);
    handleClose();
  };

  const menuItemSx = {
    fontSize: '16px',
    fontFamily: '"Segoe UI", sans-serif !important',
    '&.Mui-selected': { backgroundColor: 'transparent !important' },
    '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
  };

  return (
    <div>
      <Button
        id="time-settings-button"
        aria-controls={open ? 'time-settings-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<ExpandDropdownIcon isOpen={open} />}
        disableRipple
        variant="text"
        sx={{
          backgroundColor: 'white',
          width: '240px',
          height: '31px',
          fontWeight: '400',
          padding: '5px',
          paddingLeft: '10px',
          justifyContent: 'space-between',
          textTransform: 'none',
          border: 'none',
          boxShadow: 'none',
          fontSize: '16px',
          color: 'black',
          fontFamily: '"Segoe UI", sans-serif !important',
          borderRadius: '1px',
          '&:hover': { backgroundColor: 'white' },
          '&:focus': { outline: 'none', boxShadow: 'none' },
          '&:focus-visible': { outline: 'none', boxShadow: 'none' },
        }}
      >
        {displayLabel}
      </Button>
      <Menu
        id="time-settings-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ marginTop: '5px' }}
        MenuListProps={{
          'aria-labelledby': 'time-settings-button',
          sx: { width: '220px', fontSize: '16px', padding: '0px' },
        }}
        PaperProps={{ sx: { width: '220px' } }}
      >
        <ListSubheader sx={{ fontSize: '16px', color: '#8E979E', height: '37px' }}>
          Time Zone
        </ListSubheader>
        {options[0].values.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === timeZone}
            onClick={() => selectTimeZone(option.value)}
            sx={menuItemSx}
          >
            <Radio
              style={{ color: '#95C6EF', padding: 2 }}
              checked={option.value === timeZone}
              size="small"
            />
            <Box component="span" sx={{ ml: 1 }}>
              {option.label}
            </Box>
          </MenuItem>
        ))}
        <ListSubheader sx={{ fontSize: '16px', color: '#8E979E', height: '37px' }}>
          Time Format
        </ListSubheader>
        {options[1].values.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === timeFormat}
            onClick={() => selectTimeFormat(option.value)}
            sx={menuItemSx}
          >
            <Radio
              style={{ color: '#95C6EF', padding: 2 }}
              checked={option.value === timeFormat}
              size="small"
            />
            <Box component="span" sx={{ ml: 1 }}>
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default TimeSettingsDropDown;
